import { useEffect, useRef, useState } from "react";
import { ActionIcon, Anchor, Button, Group, Indicator, Popover, ScrollArea, Stack, Text } from "@mantine/core";
import { IconBell } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { api } from "../api/client";
import type { Notification } from "../api/types";

const POLL_MS = 30_000;

export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const timerRef = useRef<number | null>(null);

  async function refresh() {
    try {
      const res = await api.listNotifications({ unread: true, limit: 20 });
      setItems(res.notifications);
      setUnread(res.unread_count);
    } catch {
      // ignore — keep last state
    }
  }

  useEffect(() => {
    void refresh();
    timerRef.current = window.setInterval(() => void refresh(), POLL_MS);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, []);

  async function openItem(n: Notification) {
    try {
      await api.markNotificationRead(n.id);
    } catch {
      // ignore — navigation still happens
    }
    setOpen(false);
    void refresh();
    if (n.link) navigate(n.link);
  }

  async function readAll() {
    await api.markAllNotificationsRead();
    await refresh();
  }

  return (
    <Popover opened={open} onChange={setOpen} position="bottom-end" width={400} shadow="md">
      <Popover.Target>
        <Indicator label={unread > 99 ? "99+" : unread} size={16} disabled={unread === 0} color="red" offset={4}>
          <ActionIcon
            variant="subtle"
            onClick={() => {
              setOpen((o) => !o);
              if (!open) void refresh();
            }}
            aria-label={t("notifications.bellAria")}
          >
            <IconBell size={20} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Stack gap={0}>
          <Group justify="space-between" p="sm">
            <Text fw={600}>{t("notifications.title")}</Text>
            <Anchor component="button" type="button" size="xs" onClick={() => void readAll()}>
              {t("notifications.markAllRead")}
            </Anchor>
          </Group>
          <ScrollArea h={items.length === 0 ? 80 : 360}>
            {items.length === 0 ? (
              <Text c="dimmed" size="sm" p="sm">{t("notifications.empty")}</Text>
            ) : (
              <Stack gap={0}>
                {items.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => void openItem(n)}
                    style={{
                      textAlign: "left",
                      padding: 12,
                      border: 0,
                      background: n.read_at ? "transparent" : "var(--mantine-color-blue-light)",
                      borderTop: "1px solid var(--mantine-color-default-border)",
                      cursor: "pointer",
                    }}
                  >
                    <Text fw={n.read_at ? 400 : 600} size="sm">{n.title}</Text>
                    <Text size="xs" c="dimmed" lineClamp={2}>{n.body}</Text>
                    <Text size="xs" c="dimmed" mt={4}>
                      {new Date(n.created_at).toLocaleString()}
                    </Text>
                  </button>
                ))}
              </Stack>
            )}
          </ScrollArea>
          <Group justify="center" p="xs" style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}>
            <Button
              variant="subtle"
              size="xs"
              onClick={() => {
                setOpen(false);
                navigate("/notifications");
              }}
            >
              {t("notifications.viewAll")}
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
